import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Select, message } from 'antd';
import { collection, doc, setDoc, getDocs } from 'firebase/firestore';
import { useFirebase } from '../FirebaseContext';
import {
  TextB,
  TextItalic,
  TextUnderline,
  Eye,
  EyeSlash,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import './AdminNotificationSender.css';

const { TextArea } = Input;
const { Option } = Select;

const AdminNotificationSender = () => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [textContent, setTextContent] = useState('');
  const textAreaRef = useRef(null);

  const { currentFirebase } = useFirebase();
  const db = currentFirebase.firestore;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersList = usersSnapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            firstName: data.firstName || 'Brak',
            lastName: data.lastName || '',
            email: data.email || '',
          };
        });
        setUsers(usersList);
      } catch (error) {
        console.error('Error fetching users:', error);
        message.error('Błąd podczas ładowania użytkowników.');
      }
    };
    fetchUsers();
  }, [db]);

  const insertFormatting = (startTag, endTag) => {
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const sel = textarea.value.substring(start, end);
    const before = textarea.value.substring(0, start);
    const after = textarea.value.substring(end);
    const newText = before + startTag + sel + endTag + after;
    setTextContent(newText);
    form.setFieldValue('text', newText);
    setTimeout(() => {
      textarea.focus();
      const pos = sel ? start + startTag.length + sel.length + endTag.length : start + startTag.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  // Preview renders admin-authored content only (not user input),
  // so XSS risk is acceptable here — admin controls the content.
  const formatTextForPreview = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>')
      .replace(/\n/g, '<br/>');
  };

  const onFinish = async (values) => {
    setIsSending(true);
    try {
      const promises = selectedUsers.map(async (userId) => {
        const notificationRef = doc(collection(db, `users/${userId}/notifications`));
        await setDoc(notificationRef, {
          title: values.title,
          text: values.text,
          originalText: values.text,
          createdAt: new Date(),
          isRead: false,
          hasFormatting: true,
        });
      });
      await Promise.all(promises);
      message.success('Powiadomienia wysłane!');
      form.resetFields();
      setTextContent('');
      setShowPreview(false);
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error sending notifications:', error);
      message.error('Błąd podczas wysyłania.');
    } finally {
      setIsSending(false);
    }
  };

  const onUserSelectChange = (value) => {
    if (value.includes('all')) {
      setSelectedUsers(users.map(u => u.id));
    } else {
      setSelectedUsers(value.filter(v => v !== 'all'));
    }
  };

  return (
    <div className="notif-page">
      <Form form={form} layout="vertical" onFinish={onFinish} className="notif-form">

        {/* Recipients */}
        <Form.Item
          name="users"
          label="Odbiorcy"
          rules={[{ required: true, message: 'Wybierz przynajmniej jednego użytkownika' }]}
        >
          <Select
            mode="multiple"
            placeholder="Wybierz użytkowników..."
            onChange={onUserSelectChange}
            optionLabelProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={selectedUsers}
            maxTagCount={5}
            maxTagPlaceholder={(omitted) => `+${omitted.length}`}
          >
            <Option key="all" value="all" label="Wszyscy">
              <span style={{ fontWeight: 600 }}>Wszyscy ({users.length})</span>
            </Option>
            {users.map(u => (
              <Option key={u.id} value={u.id} label={`${u.firstName} ${u.lastName}`}>
                {u.firstName} {u.lastName}
                {u.email && <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: 12 }}>{u.email}</span>}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Title */}
        <Form.Item
          name="title"
          label="Tytuł"
          rules={[{ required: true, message: 'Wprowadź tytuł' }]}
        >
          <Input placeholder="Tytuł powiadomienia" />
        </Form.Item>

        {/* Text with toolbar */}
        <Form.Item
          name="text"
          label="Treść"
          rules={[{ required: true, message: 'Wprowadź treść' }]}
        >
          <div className="notif-editor">
            <div className="notif-toolbar">
              <button type="button" className="notif-tool-btn" onClick={() => insertFormatting('**', '**')} title="Pogrubienie (Ctrl+B)">
                <TextB size={16} weight="bold" />
              </button>
              <button type="button" className="notif-tool-btn" onClick={() => insertFormatting('_', '_')} title="Kursywa (Ctrl+I)">
                <TextItalic size={16} weight="bold" />
              </button>
              <button type="button" className="notif-tool-btn" onClick={() => insertFormatting('<u>', '</u>')} title="Podkreślenie (Ctrl+U)">
                <TextUnderline size={16} weight="bold" />
              </button>
              <div className="notif-tool-sep" />
              <button
                type="button"
                className={`notif-tool-btn${showPreview ? ' active' : ''}`}
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? <EyeSlash size={16} /> : <Eye size={16} />}
                <span style={{ fontSize: 12, marginLeft: 4 }}>Podgląd</span>
              </button>
            </div>

            <TextArea
              ref={textAreaRef}
              rows={6}
              placeholder="Treść powiadomienia..."
              value={textContent}
              onChange={(e) => { setTextContent(e.target.value); form.setFieldValue('text', e.target.value); }}
              className="notif-textarea"
              onKeyDown={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  if (e.key === 'b') { e.preventDefault(); insertFormatting('**', '**'); }
                  if (e.key === 'i') { e.preventDefault(); insertFormatting('_', '_'); }
                  if (e.key === 'u') { e.preventDefault(); insertFormatting('<u>', '</u>'); }
                }
              }}
            />

            {showPreview && textContent && (
              <div className="notif-preview">
                <div className="notif-preview-label">Podgląd</div>
                {/* Admin-only content — safe to render */}
                <div
                  className="notif-preview-body"
                  dangerouslySetInnerHTML={{ __html: formatTextForPreview(textContent) }}
                />
              </div>
            )}
          </div>
        </Form.Item>

        {/* Submit */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSending}
            disabled={selectedUsers.length === 0}
            className="notif-submit"
          >
            <PaperPlaneTilt size={16} weight="bold" />
            {isSending ? 'Wysyłanie...' : `Wyślij (${selectedUsers.length})`}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AdminNotificationSender;
